import React, { lazy, Suspense, useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { App as F7App, View } from "framework7-react";
import Framework7 from "framework7/lite-bundle";
import Framework7Lite from "framework7/lite";
import Dialog from "framework7/components/dialog";
import Toast from "framework7/components/toast";
import Preloader from "framework7/components/preloader";
import Framework7React from "framework7-react";
import { RecoilRoot, useRecoilValue } from "recoil";
import { HelmetProvider } from "react-helmet-async";
import AuthState from "./atoms/AuthState.jsx";

import "./css/app.scss";
import "./css/variables.scss";
import "./css/launch-deposit.scss";

import Loading from "./component/Loading.jsx";
import Toolbar from "./component/toolbar.jsx";
import ScrollToTop from "./component/ScrollToTop.jsx";
import SidePanel from "./component/SidePanel.jsx";
import { setupPreventDevTools } from "./utils/preventDevTools.js";
import { GlobalDataProvider } from "./context/GlobalDataContext.jsx";
import MetaFromGlobal from "./component/MetaFromGlobal.jsx";
import ChatSDKManager from "./component/ChatSDKManager.jsx";
import useCommon from "./component/action/Common.jsx";
import { loadLanguage, setLanguage } from "./component/action/preferences.jsx";
import i18n from "./i18n";

/* framework7-react constructs the app from `framework7/lite`, not lite-bundle.
   Register modal UI modules on that class or f7.dialog / f7.toast / f7.preloader stay missing. */
Framework7Lite.use([Dialog, Toast, Preloader]);
Framework7.use(Framework7React);

// Lazy imports
const MobileHeader = lazy(() => import("./component/mobile/header.jsx"));
const WebHeader = lazy(() => import("./component/web/header.jsx"));
const MobileHome = lazy(() => import("./pages/mobile/home.jsx"));
const WebHome = lazy(() => import("./pages/web/home.jsx"));
const WebCasino = lazy(() => import("./pages/web/casino.jsx"));
const MobileCasino = lazy(() => import("./pages/mobile/casino.jsx"));
const WebFooter = lazy(() => import("./component/web/footer.jsx"));
const MobileFooter = lazy(() => import("./component/mobile/footer.jsx"));
const VipRanking = lazy(() => import("./pages/vip-ranking.jsx"));
const Promotion = lazy(() => import("./pages/promotion.jsx"));
const Poker = lazy(() => import("./pages/poker.jsx"));
const WebSlots = lazy(() => import("./pages/web/slots.jsx"));
const MobileSlots = lazy(() => import("./pages/mobile/slots.jsx"));
const Profile = lazy(() => import("./pages/profile.jsx"));
const NewProfile = lazy(() => import("./pages/new-profile.jsx"));
const Deposit = lazy(() => import("./pages/deposit.jsx"));
const LaunchDeposit = lazy(() => import("./pages/launch-deposit.jsx"));
const Withdraw = lazy(() => import("./pages/withdraw.jsx"));
const WebFastGames = lazy(() => import("./pages/web/fast-games.jsx"));
const MobileFastGames = lazy(() => import("./pages/mobile/fast-games.jsx"));
const DailyPrizes = lazy(() => import("./pages/daily-prizes.jsx"));
const Transaction = lazy(() => import("./pages/transaction.jsx"));
const ProfileDetails = lazy(() => import("./pages/profile-details.jsx"));
const ChangePasswordPage = lazy(() => import("./pages/change-password.jsx"));
const ProfileReferral = lazy(() => import("./pages/referral.jsx"));
const LiveChatPage = lazy(() => import("./pages/live-chat.jsx"));
const Reward = lazy(() => import("./pages/reward.jsx"));

const f7params = {
  name: "My App",
  theme: "auto",
  serviceWorker: {
    path: "/service-worker.js",
  },
  panel: {
    backdrop: true,
    backdropEl: ".panel-backdrop",
    swipe: false,
  },
  view: {
    stackPages: false, // prevent scroll freeze
    browserHistory: false, // avoid conflicts with Router
  },
  iosTranslucentBars: false,
  iosTranslucentModals: false,
};

// Must live inside Router so useLocation() is valid. Calls refresh_user on route change and tab visible.
const RefreshUserOnNavigate = () => {
  const { pathname } = useLocation();
  const { refresh_user } = useCommon();
  const refreshUserRef = useRef(refresh_user);
  refreshUserRef.current = refresh_user;

  // Run only when pathname changes (not when refresh_user reference changes)
  useEffect(() => {
    refreshUserRef.current();
  }, [pathname]);

  // Run once on mount; listener always calls latest refresh_user via ref
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshUserRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return null;
};

const FooterSlot = ({ isWeb }) => {
  const { pathname } = useLocation();
  if (pathname === "/live_chat" || pathname === "/launch_deposit" || pathname === "/deposit") {
    return null;
  }
  return (
    <Suspense fallback={null}>
      {isWeb ? <WebFooter /> : <MobileFooter />}
    </Suspense>
  );
};

const App = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isWeb = useMediaQuery({ minWidth: 769 });

  const showSidebar = useMediaQuery({ minWidth: 501 });
  const showToolbar = useMediaQuery({ maxWidth: 500 });
  const authState = useRecoilValue(AuthState);
  const isLoggedIn = !!authState?.token;
  const [tabletSidebarExpanded, setTabletSidebarExpanded] = useState(false);

  useEffect(() => {
    const supportedLngs = i18n.supportedLngs || ["en", "zh", "ms"];
    const applyLanguage = (lng) => {
      if (lng && supportedLngs.includes(lng)) {
        i18n.changeLanguage(lng);
      } else {
        i18n.changeLanguage("en");
        setLanguage("en");
      }
    };
    loadLanguage()
      .then(applyLanguage)
      .catch(() => applyLanguage(null));
  }, []);

  // Lock body/content scroll when tablet side panel is open
  const tabletPanelOpen = showSidebar && !isWeb && tabletSidebarExpanded;
  useEffect(() => {
    if (!tabletPanelOpen) return;
    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      const savedScrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      const y = savedScrollY ? Math.abs(parseInt(savedScrollY, 10) || 0) : 0;
      window.scrollTo(0, y);
    };
  }, [tabletPanelOpen]);

  // Disable scroll restoration (must-have for mobile)
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  // useEffect(() => setupPreventDevTools(), []);

  return (
    <RecoilRoot>
      <HelmetProvider>
        <Router>
          <RefreshUserOnNavigate />
          <ScrollToTop />

          <F7App {...f7params}>
            <View main className="safe-areas">
              <GlobalDataProvider>
                <MetaFromGlobal />
                <ChatSDKManager />
                {showSidebar ? (
                  <div
                    className={`web-layout-with-sidebar ${!isWeb && tabletSidebarExpanded ? "web-layout-with-sidebar-overlay" : ""}`}
                  >
                    <header className="web-layout-header">
                      <Suspense fallback={<Loading />}>
                        {isWeb ? <WebHeader /> : <MobileHeader />}
                      </Suspense>
                    </header>
                    <div className="web-below-header">
                      <div className="web-side-panel-wrap">
                        <SidePanel
                          isWeb={isWeb}
                          defaultExpanded={isWeb}
                          expanded={isWeb ? undefined : tabletSidebarExpanded}
                          onExpandedChange={
                            isWeb ? undefined : setTabletSidebarExpanded
                          }
                        />
                      </div>
                      <div
                        className="web-side-panel-content-wrap"
                        onClick={(e) => {
                          if (
                            !isWeb &&
                            tabletSidebarExpanded &&
                            e.target === e.currentTarget
                          ) {
                            setTabletSidebarExpanded(false);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            !isWeb &&
                            tabletSidebarExpanded &&
                            e.key === "Escape"
                          ) {
                            setTabletSidebarExpanded(false);
                          }
                        }}
                        role={
                          !isWeb && tabletSidebarExpanded ? "button" : undefined
                        }
                        tabIndex={
                          !isWeb && tabletSidebarExpanded ? 0 : undefined
                        }
                        aria-label={
                          !isWeb && tabletSidebarExpanded
                            ? "Close sidebar"
                            : undefined
                        }
                      >
                        <div className="web-side-panel-content">
                          <Suspense fallback={<Loading />}>
                            <Routes>
                              <Route
                                path="/"
                                element={
                                  isMobile ? <MobileHome /> : <WebHome />
                                }
                              />
                              <Route
                                path="/home"
                                element={
                                  isMobile ? <MobileHome /> : <WebHome />
                                }
                              />
                              <Route
                                path="/casino"
                                element={
                                  isMobile ? <MobileCasino /> : <WebCasino />
                                }
                              />
                              <Route
                                path="/vip-ranking"
                                element={<VipRanking />}
                              />
                              <Route
                                path="/promotion"
                                element={<Promotion />}
                              />
                              <Route path="/poker" element={<Poker />} />
                              <Route
                                path="/slots"
                                element={
                                  isMobile ? <MobileSlots /> : <WebSlots />
                                }
                              />
                              <Route path="/profile" element={<NewProfile />} />
                              <Route path="/deposit" element={<Deposit />} />
                              <Route
                                path="/launch_deposit"
                                element={<LaunchDeposit />}
                              />
                              <Route path="/withdraw" element={<Withdraw />} />
                              <Route
                                path="/fast-games"
                                element={
                                  isMobile ? (
                                    <MobileFastGames />
                                  ) : (
                                    <WebFastGames />
                                  )
                                }
                              />
                              <Route
                                path="/daily-prizes"
                                element={<DailyPrizes />}
                              />
                              <Route
                                path="/transaction"
                                element={<Transaction />}
                              />
                              <Route
                                path="/profile-details"
                                element={<ProfileDetails />}
                              />
                              <Route
                                path="/change-password"
                                element={<ChangePasswordPage />}
                              />
                              <Route
                                path="/referral"
                                element={<ProfileReferral />}
                              />
                              <Route
                                path="/live_chat"
                                element={<LiveChatPage />}
                              />
                              <Route path="/reward" element={<Reward />} />
                            </Routes>
                          </Suspense>
                          <FooterSlot isWeb={isWeb} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Suspense fallback={<Loading />}>
                      {isWeb ? <WebHeader /> : <MobileHeader />}
                    </Suspense>

                    <Suspense fallback={<Loading />}>
                      <Routes>
                        <Route
                          path="/"
                          element={isMobile ? <MobileHome /> : <WebHome />}
                        />
                        <Route
                          path="/home"
                          element={isMobile ? <MobileHome /> : <WebHome />}
                        />
                        <Route
                          path="/casino"
                          element={isMobile ? <MobileCasino /> : <WebCasino />}
                        />
                        <Route path="/vip-ranking" element={<VipRanking />} />
                        <Route path="/promotion" element={<Promotion />} />
                        <Route path="/poker" element={<Poker />} />
                        <Route
                          path="/slots"
                          element={isMobile ? <MobileSlots /> : <WebSlots />}
                        />
                        <Route path="/profile" element={<NewProfile />} />
                        <Route path="/deposit" element={<Deposit />} />
                        <Route
                          path="/launch_deposit"
                          element={<LaunchDeposit />}
                        />
                        <Route path="/withdraw" element={<Withdraw />} />
                        <Route
                          path="/fast-games"
                          element={
                            isMobile ? <MobileFastGames /> : <WebFastGames />
                          }
                        />
                        <Route path="/daily-prizes" element={<DailyPrizes />} />
                        <Route path="/transaction" element={<Transaction />} />
                        <Route
                          path="/profile-details"
                          element={<ProfileDetails />}
                        />
                        <Route
                          path="/change-password"
                          element={<ChangePasswordPage />}
                        />
                        <Route path="/referral" element={<ProfileReferral />} />
                        <Route path="/live_chat" element={<LiveChatPage />} />
                        <Route path="/reward" element={<Reward />} />
                      </Routes>
                    </Suspense>

                    <FooterSlot isWeb={isWeb} />
                  </>
                )}
              </GlobalDataProvider>
            </View>

            {/* Toolbar: only at max-width 500px */}
            {showToolbar ? <Toolbar /> : null}
          </F7App>
        </Router>
      </HelmetProvider>
    </RecoilRoot>
  );
};

export default App;
